import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core'
import SydButton from '../../../../../commonDesign/Button'
import OperationalTable from '../../../../../organisms/OperationalTable'
import StatusBadge from '../shared/StatusBadge'
import { scoreColor } from '../shared/scoreColor'

const useStyles = makeStyles(() => ({
  card: {
    border: '1px solid var(--color-neutral-200)',
    borderRadius: 'var(--border-radius-lg)',
    backgroundColor: 'white',
    padding: 'var(--padding-md)',
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--gap-sm)'
  },
  title: {
    fontSize: 'var(--text-size-sm)',
    fontWeight: 600,
    color: 'var(--color-neutral-700)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > tbody > tr > td': { padding: '10px 18px' }
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  scoreCell: {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums'
  },
  reasoning: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-700)',
    lineHeight: 1.5,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    maxWidth: '460px'
  },
  empty: {
    color: 'var(--color-neutral-400)'
  },
  errorText: {
    color: 'var(--color-danger-700, #b91c1c)'
  },
  rowExpanded: {
    backgroundColor: 'var(--color-neutral-050)',
    padding: '8px 12px',
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-700)',
    borderRadius: 'var(--border-radius-sm)',
    marginTop: '4px'
  },
  expandedHead: {
    fontWeight: 600,
    marginBottom: '6px'
  },
  jsonBlock: {
    backgroundColor: 'var(--color-neutral-050)',
    padding: '8px',
    borderRadius: 'var(--border-radius-sm)',
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: '12px',
    overflow: 'auto',
    maxHeight: '320px',
    whiteSpace: 'pre-wrap'
  },
  expandedError: {
    marginTop: 6,
    color: 'var(--color-danger-700, #b91c1c)'
  },
  expandedThread: {
    marginTop: 6
  }
}))

/* eslint-disable react/prop-types */
const buildColumns = (classes, expanded, toggleExpanded) => [
  {
    id: 'sampleIndex',
    accessor: 'sampleIndex',
    Header: '#',
    Cell: ({ value }) => <span className={classes.mono}>{value ?? '\u2014'}</span>
  },
  {
    id: 'status',
    accessor: 'status',
    Header: 'Status',
    Cell: ({ value }) => <StatusBadge status={value} />
  },
  {
    id: 'weightedScore',
    accessor: 'weightedScore',
    Header: 'Score',
    Cell: ({ value }) => {
      if (value === null || value === undefined) {
        return <span className={classes.empty}>{'\u2014'}</span>
      }
      return (
        <span className={classes.scoreCell} style={{ color: scoreColor(value * 100) }}>
          {(value * 100).toFixed(1)}%
        </span>
      )
    }
  },
  {
    id: 'reasoning',
    Header: 'Reasoning',
    disableSortBy: true,
    Cell: ({ row }) => {
      const s = row.original
      const reasoning = s.judgement?.reasoning
      if (reasoning) return <div className={classes.reasoning}>{reasoning}</div>
      if (s.judgeError) return <span className={classes.errorText}>{s.judgeError}</span>
      if (s.lastError) return <span className={classes.errorText}>{s.lastError}</span>
      return <span className={classes.empty}>{'\u2014'}</span>
    }
  },
  {
    id: 'threadId',
    accessor: 'threadId',
    Header: 'Thread',
    Cell: ({ value }) => value
      ? <a className={classes.mono} href={`/denali-ai/chat/${value}`} target='_blank' rel='noreferrer'>{'open \u2197'}</a>
      : <span className={classes.empty}>{'\u2014'}</span>
  },
  {
    id: 'actions',
    Header: '',
    disableSortBy: true,
    Cell: ({ row }) => (
      <SydButton
        size='xs'
        variant='secondary'
        onClick={() => toggleExpanded(row.original.batchTaskId)}
      >
        {expanded[row.original.batchTaskId] ? 'Hide' : 'Details'}
      </SydButton>
    )
  }
]
/* eslint-enable react/prop-types */

const DEFAULT_SORT = [{ id: 'sampleIndex', desc: false }]

function SamplesTable ({ samples }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState({})

  const toggleExpanded = useMemo(() => (id) => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }))
  }, [])

  const columns = useMemo(
    () => buildColumns(classes, expanded, toggleExpanded),
    [classes, expanded, toggleExpanded]
  )

  const expandedSamples = samples.filter(s => expanded[s.batchTaskId])

  return (
    <div className={classes.card}>
      <div className={classes.title}>Samples</div>
      <div className={classes.tableWrap}>
        <OperationalTable
          variant='blank'
          itemName='Samples'
          columns={columns}
          data={samples}
          defaultSort={DEFAULT_SORT}
        />
      </div>
      {expandedSamples.map(s => (
        <div key={s.batchTaskId} className={classes.rowExpanded}>
          <div className={classes.expandedHead}>
            Sample #{s.sampleIndex} {'\u00B7'} taskId {s.batchTaskId}
          </div>
          {s.judgement
            ? <pre className={classes.jsonBlock}>{JSON.stringify(s.judgement, null, 2)}</pre>
            : <em>No judgement recorded.</em>}
          {(s.judgeError || s.lastError) && (
            <div className={classes.expandedError}>
              Error: {s.judgeError || s.lastError}
            </div>
          )}
          {s.threadId && (
            <div className={classes.expandedThread}>
              <a href={`/denali-ai/chat/${s.threadId}`} target='_blank' rel='noreferrer'>
                {'Open assistant thread in chat \u2197'}
              </a>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

SamplesTable.propTypes = {
  samples: PropTypes.array.isRequired
}

export default SamplesTable
